import { API_CONFIG, API_ENDPOINTS, HTTP_METHODS, ApiResponse, RequestOptions } from './config';
import { tokenRefreshService } from '../../services/TokenRefreshService';
import { withWorkspaceParam, withWorkspaceQuery } from './workspaceScopedEndpoints';

export class ApiError extends Error {
  error: string;
  message: string;
  details?: any;
  code?: string;
  statusCode?: number;
  meta?: any;
  errors?: Record<string, string>;
  retryAfter?: number;

  constructor({ error, message, details, code, statusCode, meta, errors, retryAfter }: { error: any; message?: string; details?: any; code?: string; statusCode?: number; meta?: any; errors?: Record<string, string>; retryAfter?: number }) {
    let finalMessage = message;
    if (!finalMessage) {
      if (typeof error === 'string') {
        finalMessage = error;
      } else if (error && typeof error === 'object') {
        finalMessage = error.message || error.error || JSON.stringify(error);
      } else {
        finalMessage = 'An unknown error occurred';
      }
    }

    super(finalMessage);
    this.name = 'ApiError';
    this.error = typeof error === 'string' ? error : (error?.message || finalMessage);
    this.message = finalMessage;
    this.details = details;
    this.code = code;
    this.statusCode = statusCode;
    this.meta = meta;
    this.errors = errors;
    this.retryAfter = retryAfter;
  }

  static fromResult(
    result: { error?: any; message?: string; detail?: any; details?: any; code?: string; errorCode?: string; meta?: any; statusCode?: number; errors?: Record<string, string> } | null | undefined,
    fallbackMessage?: string,
  ): ApiError {
    const r = result ?? {};
    const detailMsg = typeof r.detail === 'string' ? r.detail : undefined;
    const nested =
      r.error && typeof r.error === 'object' && !Array.isArray(r.error)
        ? (r.error as { code?: string; message?: string; details?: any; meta?: any; errors?: Record<string, string> })
        : null;
    const nestedMessage = typeof nested?.message === 'string' ? nested.message : undefined;
    const stringError = typeof r.error === 'string' ? r.error : nestedMessage;
    return new ApiError({
      error: stringError ?? r.message ?? detailMsg ?? fallbackMessage ?? 'Request failed',
      message: stringError ?? r.message ?? detailMsg ?? fallbackMessage,
      details: r.details ?? nested?.details,
      code: r.code ?? r.errorCode ?? nested?.code,
      meta: r.meta ?? nested?.meta,
      statusCode: r.statusCode,
      errors: r.errors ?? nested?.errors,
      retryAfter:
        typeof (r as { retryAfter?: unknown }).retryAfter === 'number'
          ? (r as { retryAfter: number }).retryAfter
          : undefined,
    });
  }
}

class ApiClient {
  private baseUrl: string;
  private retryCount = 0;
  private maxRetries = 1;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
  }

  private isPublicEndpoint(endpoint: string): boolean {
    const publicEndpoints = [
      '/scheduling/event-types/slug/',
      '/scheduling/event-types/org/',
      '/scheduling/event-types/', // Public for viewing event details on booking success
      '/scheduling/bookings', // Public for creating and viewing bookings
      '/scheduling/availability/',
    ];
    return publicEndpoints.some(publicPath => endpoint.includes(publicPath));
  }

  private async makeRequest<T = any>(
    endpoint: string,
    options: RequestOptions = {},
    isRetry: boolean = false
  ): Promise<ApiResponse<T>> {
    const {
      method = 'GET',
      headers = {},
      body,
    } = options;

    const url = `${this.baseUrl}${endpoint}`;

    const isPublic = this.isPublicEndpoint(endpoint);

    if (!isRetry && !isPublic) {
      await tokenRefreshService.ensureValidToken();
    }

    const authHeaders: Record<string, string> = {
      ...headers,
    };

    const isFormData = body instanceof FormData;
    if (body && !isFormData) {
      authHeaders['Content-Type'] = 'application/json';
    }

    const token = localStorage.getItem('auth_token');
    if (token) {
      authHeaders['Authorization'] = `Bearer ${token}`;
    }

    const requestOptions: RequestInit = {
      method: HTTP_METHODS[method],
      headers: authHeaders,
      body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
    };

    try {
      const response = await fetch(url, requestOptions);

      if (response.status === 401 && !isRetry && this.retryCount < this.maxRetries) {
        this.retryCount++;

        const refreshOutcome = await tokenRefreshService.forceRefresh();

        if (refreshOutcome === 'refreshed') {
          this.retryCount = 0;
          return this.makeRequest<T>(endpoint, options, true);
        }

        if (refreshOutcome === 'unavailable') {
          this.retryCount = 0;
          console.warn(
            '[apiClient] Session check unavailable after a 401 — keeping the user signed in and surfacing a transient error instead of redirecting to /auth.',
            {
              endpoint,
              originalStatus: response.status,
              requestId: tokenRefreshService.getLastRefreshRequestId(),
            }
          );
          throw new ApiError({
            error: "We're having a temporary server problem. Please try again in a moment.",
            message: "We're having a temporary server problem. Please try again in a moment.",
            code: 'SESSION_CHECK_UNAVAILABLE',
            statusCode: 503,
          });
        }

        {
          let pwChangeGrace = false;
          try {
            const ts = Number(sessionStorage.getItem('pw_change_logout_ts') || '');
            pwChangeGrace = Number.isFinite(ts) && Date.now() - ts < 6000;
          } catch {
          }
          if (!pwChangeGrace) {
            console.error(
              '[apiClient] Logging user out: refresh failed after a 401 on request, and no password-change grace window is active. This is the trigger for the redirect to /auth. Search backend + frontend logs for this requestId to see the full story.',
              {
                endpoint,
                originalStatus: response.status,
                requestId: tokenRefreshService.getLastRefreshRequestId(),
              }
            );
            tokenRefreshService.clearTokens();
            window.location.href = '/auth';
          } else {
            console.warn(
              '[apiClient] Refresh failed but password-change grace window is active — suppressing redirect this time.',
              { endpoint, requestId: tokenRefreshService.getLastRefreshRequestId() }
            );
          }
          return {
            data: null,
            error: 'Session expired - please login again',
            success: false,
          };
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        const isExpected404 = response.status === 404 && endpoint.startsWith('/ads/');
        if (!isExpected404) {
          console.error(`API Error [${response.status}]:`, errorData);
        }

        const retryAfterHeader = response.headers?.get?.('retry-after');
        const retryAfterSeconds = retryAfterHeader ? Number(retryAfterHeader) : NaN;

        throw new ApiError({
          error: errorData.error || errorData.message || `HTTP ${response.status}`,
          message: errorData.message || errorData.error?.message || errorData.error || response.statusText,
          details: errorData?.details,
          statusCode: response.status,
          meta: errorData?.meta,
          errors: errorData?.errors,
          code: errorData.code || errorData.error?.code,
          retryAfter:
            Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
              ? retryAfterSeconds
              : undefined,
        });
      }

      this.retryCount = 0;

      const data = await response.json();

      if (data.success !== undefined) {
        let payload = data.data;
        if (payload === undefined) {
          if (Object.prototype.hasOwnProperty.call(data, 'template')) {
            payload = data.template;
          } else if (Object.prototype.hasOwnProperty.call(data, 'templates')) {
            payload = data.templates;
          } else if (Object.prototype.hasOwnProperty.call(data, 'account')) {
            payload = data.account;
          } else if (Object.prototype.hasOwnProperty.call(data, 'accounts')) {
            payload = data.accounts;
          } else if (Object.prototype.hasOwnProperty.call(data, 'scheduledPosts')) {
            payload = data.scheduledPosts;
          } else if ((Object.prototype.hasOwnProperty.call(data, 'tempSessionId') && Object.prototype.hasOwnProperty.call(data, 'availableAccounts')) ||
            (Object.prototype.hasOwnProperty.call(data, 'tempToken') && Object.prototype.hasOwnProperty.call(data, 'availableAccounts'))) {
            payload = data;
          }
        }
        if (payload === undefined) {
          payload = data;
        }
        return {
          data: payload,
          error: data.success ? null : (typeof data.error === 'object' ? data.error?.message : data.error),
          count: data.meta?.total ?? data.meta?.count ?? data.count,
          totalCount: data.meta?.total ?? data.totalCount ?? data.total,
          filteredTotalCount: data.filteredTotalCount,
          hasMore: data.hasMore ?? data.meta?.hasMore,
          message: data.message,
          success: data.success,
          details: data.details,
          unreadCount: data.unreadCount,
          alreadyCustomer: data.alreadyCustomer,
          done: data.done,
          deletedRecipients: data.deletedRecipients,
          pagination: data.pagination,
          flow: data.flow,
          stats: data.stats,
          nextPageToken: data.nextPageToken ?? data.meta?.nextPageToken ?? null,
          limit: data.limit ?? data.meta?.limit,
        };
      } else {
        return {
          data: data.data || data,
          error: null,
          count: data.count,
          totalCount: data.totalCount,
          filteredTotalCount: data.filteredTotalCount,
          hasMore: data.hasMore,
          message: data.message,
          success: true,
          details: data.details,
        };
      }
    } catch (error) {
      let errorMessage = 'Unknown error';
      let errorDetails = undefined;
      let errorMeta = undefined;
      let errorCode: string | undefined = undefined;
      let errorFields: Record<string, string> | undefined = undefined;
      let errorStatus: number | undefined = undefined;

      if (error instanceof ApiError) {
        errorMessage = error.message;
        errorDetails = error.details;
        errorMeta = error.meta;
        errorFields = error.errors;
        errorStatus = error.statusCode;
        if (typeof error.code === 'string' && error.code) {
          errorCode = error.code;
        } else if (typeof error.error === 'string' && error.error && error.error !== error.message) {
          errorCode = error.error;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return {
        data: null,
        error: errorMessage,
        code: errorCode,
        message: errorMessage,
        details: errorDetails,
        meta: errorMeta,
        errors: errorFields,
        statusCode: errorStatus,
        success: false
      };
    }
  }


  async get<T = any>(endpoint: string, options: Omit<RequestOptions, 'method' | 'body'> & { params?: Record<string, any> } = {}): Promise<ApiResponse<T>> {
    const { params: rawParams, ...requestOptions } = options;
    const params = withWorkspaceParam(endpoint, rawParams);

    let fullEndpoint = endpoint;
    if (params && Object.keys(params).length > 0) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(item => {
              queryParams.append(key, String(item));
            });
          } else {
            queryParams.append(key, String(value));
          }
        }
      });
      const sep = endpoint.includes('?') ? '&' : '?';
      fullEndpoint = `${endpoint}${sep}${queryParams.toString()}`;
    }

    return this.makeRequest<T>(fullEndpoint, { ...requestOptions, method: 'GET' });
  }

  async post<T = any>(endpoint: string, body?: any, options: Omit<RequestOptions, 'method'> & { params?: Record<string, any> } = {}): Promise<ApiResponse<T>> {
    const { params: rawParams, ...requestOptions } = options;
    const params = withWorkspaceParam(endpoint, rawParams, body);

    let fullEndpoint = endpoint;
    if (params && Object.keys(params).length > 0) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
      const sep = endpoint.includes('?') ? '&' : '?';
      fullEndpoint = `${endpoint}${sep}${queryParams.toString()}`;
    }

    return this.makeRequest<T>(fullEndpoint, { ...requestOptions, method: 'POST', body });
  }

  async put<T = any>(endpoint: string, body?: any, options: Omit<RequestOptions, 'method'> = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(withWorkspaceQuery(endpoint, body), { ...options, method: 'PUT', body });
  }

  async patch<T = any>(endpoint: string, body?: any, options: Omit<RequestOptions, 'method'> = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(withWorkspaceQuery(endpoint, body), { ...options, method: 'PATCH', body });
  }

  async delete<T = any>(endpoint: string, options: Omit<RequestOptions, 'method'> = {}): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(withWorkspaceQuery(endpoint), { ...options, method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();

export { ApiClient };
